/**
 * Bundle 48.5 - circle-crop dialog for host photo uploads.
 *
 * Public event pages render host thumbnails inside a 56px circle
 * (rounded-full + object-cover). Uploading a rectangular image without
 * pre-cropping either distorts or awkwardly centres the subject. This
 * dialog lets the admin pick a 1:1 square crop before upload.
 *
 * The final blob is emitted at 512x512 JPEG (well above the 56px
 * render size, still small enough that GridFS keeps under 100KB).
 */
import React, { useCallback, useEffect, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

const OUTPUT_SIZE = 512;

const _getCroppedBlob = async (imageSrc, area, mimeIn) => {
  // Load image
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    area.x, area.y, area.width, area.height,
    0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
  );
  const outMime = mimeIn === 'image/png' ? 'image/png' : 'image/jpeg';
  return await new Promise((resolve) => canvas.toBlob(resolve, outMime, 0.9));
};

const HostPhotoCropperDialog = ({ file, open, onCancel, onConfirm }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelArea, setPixelArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file || !open) { setImageSrc(null); return; }
    const reader = new FileReader();
    reader.onload = (e) => setImageSrc(e.target?.result);
    reader.readAsDataURL(file);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setPixelArea(null);
  }, [file, open]);

  const onCropComplete = useCallback((_area, areaPx) => setPixelArea(areaPx), []);

  const apply = async () => {
    if (!imageSrc || !pixelArea || !file) return;
    setBusy(true);
    try {
      const blob = await _getCroppedBlob(imageSrc, pixelArea, file.type);
      if (!blob) throw new Error('Crop failed');
      const outFile = new File(
        [blob],
        (file.name || 'host-photo').replace(/\.[^.]+$/, '') + '.jpg',
        { type: blob.type || 'image/jpeg' },
      );
      onConfirm(outFile);
    } catch (_e) {
      // Fall back to raw file if canvas fails.
      onConfirm(file);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="host-photo-cropper-dialog">
        <DialogHeader>
          <DialogTitle>Crop host photo</DialogTitle>
          <DialogDescription>
            Public event pages render this inside a circle. Pinch, drag and zoom
            to pick the visible area.
          </DialogDescription>
        </DialogHeader>
        {imageSrc && (
          <div className="relative w-full bg-neutral-900 rounded-md"
               style={{ height: 320 }}
               data-testid="host-photo-cropper-canvas">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="cover"
            />
          </div>
        )}
        <div className="mt-3 flex items-center gap-3 text-xs">
          <span className="text-neutral-500 w-10">Zoom</span>
          <Slider
            min={1}
            max={3}
            step={0.01}
            value={[zoom]}
            onValueChange={([v]) => setZoom(v)}
            className="flex-1"
            data-testid="host-photo-cropper-zoom"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            data-testid="host-photo-cropper-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={apply}
            disabled={busy || !pixelArea}
            data-testid="host-photo-cropper-apply"
          >
            {busy ? 'Cropping...' : 'Apply crop'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HostPhotoCropperDialog;
